/**
 * Brand Safety report constants.
 *
 * Severity keys, ordering and display labels for the report UI. Colours live in
 * `static/brandSafety.scss` as `$bs-severity-*` tokens — keep the class-name suffixes here in sync
 * with the modifier names there (`.bs-dot--severe`, `.bs-severity-bar__seg--moderate`, …).
 *
 * NOTE: this module is intentionally separate from `Persona/BrandSafety/brandSafetyConsts.js`, which
 * describes the *legacy* per-post flag model (`AlcoholFlag`, `HateFlag`, …). This report works on the
 * newer severity/subclass model coming from the vision pipeline.
 */

// ─── Severity ─────────────────────────────────────────────────────────────────
export const SEVERITY = {
  SEVERE: 'severe',
  MODERATE: 'moderate',
  LOW: 'low',
  MINOR: 'minor',
};

/** Render order — highest severity first, matching the Figma card row. */
export const SEVERITY_ORDER = [SEVERITY.SEVERE, SEVERITY.MODERATE, SEVERITY.LOW, SEVERITY.MINOR];

/**
 * The API expresses per-detection severity as raw ML levels `L1`–`L4` (higher = worse; only L1/L2
 * observed so far). Map them onto our four buckets: L1 clean/incidental → minor, up to L4 → severe.
 */
export const SEVERITY_BY_LEVEL = {
  L1: SEVERITY.MINOR,
  L2: SEVERITY.LOW,
  L3: SEVERITY.MODERATE,
  L4: SEVERITY.SEVERE,
};

export const SEVERITY_LABELS = {
  [SEVERITY.SEVERE]: 'Severe',
  [SEVERITY.MODERATE]: 'Moderate',
  [SEVERITY.LOW]: 'Low',
  [SEVERITY.MINOR]: 'Minor',
};

/**
 * Copy for the `?` affordance on each severity card. Plain strings so the tooltip stays
 * translatable and testable.
 */
export const SEVERITY_HINTS = {
  [SEVERITY.SEVERE]: 'Content most brands treat as disqualifying. Review before approving.',
  [SEVERITY.MODERATE]: 'Content many brands restrict. Worth a look in context.',
  [SEVERITY.LOW]: 'Content only some brands restrict, usually category-dependent.',
  [SEVERITY.MINOR]: 'Incidental detections, typically background or third-party content.',
};

/**
 * Copy for the info-icon tooltips on the trademark table's placement columns (Primary/Active/
 * Background). Plain strings so the tooltip stays translatable and testable, and doubles as the
 * icon button's `aria-label`.
 */
export const TRADEMARK_PLACEMENT_HINTS = {
  primary: 'Primary: Means user/subject is talking or showing the product.',
  active:
    'Active: Creator is reviewing or discussing other content but actively using the product (e.g., wearing branded shoes, a branded watch, or a branded t-shirt).',
  background:
    'Background: If a logo is detected in the background — for example, someone sitting in a branded café.',
};

// ─── Detections tables ──────────────────────────────────────────────────────────
// Rows per page for the Object Detection and Trademark tables. Client-side paginated via
// usePaginatedRows so long detection lists stay scannable; the pager only appears above this count.
export const DETECTIONS_PAGE_SIZE = 12;

// ─── Analysis state ───────────────────────────────────────────────────────────
export const ANALYSIS_STATE = {
  COMPLETE: 'complete',
  PROCESSING: 'processing',
  FAILED: 'failed',
};

export const ANALYSIS_STATE_LABELS = {
  [ANALYSIS_STATE.COMPLETE]: 'Analysis complete',
  [ANALYSIS_STATE.PROCESSING]: 'Analysis in progress',
  [ANALYSIS_STATE.FAILED]: 'Analysis failed',
};

/**
 * Raw check `status` (queued · downloading_posts · running · succeeded · partially_succeeded ·
 * failed · cancelled) → the three UI analysis states. Anything non-terminal reads as "processing" so
 * progress renders from the same report shape.
 */
export const ANALYSIS_STATE_BY_CHECK_STATUS = {
  succeeded: ANALYSIS_STATE.COMPLETE,
  partially_succeeded: ANALYSIS_STATE.COMPLETE,
  failed: ANALYSIS_STATE.FAILED,
  cancelled: ANALYSIS_STATE.FAILED,
  queued: ANALYSIS_STATE.PROCESSING,
  downloading_posts: ANALYSIS_STATE.PROCESSING,
  running: ANALYSIS_STATE.PROCESSING,
};

// ─── Review queue status ──────────────────────────────────────────────────────
// The three-way split rendered by BrandSafetyStatusBar.
export const REVIEW_STATUS = {
  APPROVED: 'approved',
  PENDING: 'pending',
  REJECTED: 'rejected',
};

/** Bar segment order: approved (green) · pending (blue) · rejected (red), per the design. */
export const REVIEW_STATUS_ORDER = [REVIEW_STATUS.APPROVED, REVIEW_STATUS.PENDING];

export const REVIEW_STATUS_LABELS = {
  [REVIEW_STATUS.APPROVED]: 'Approved',
  [REVIEW_STATUS.PENDING]: 'Pending',
  [REVIEW_STATUS.REJECTED]: 'Rejected',
};

// ─── Filters panel ("Brand Safety Parameters") ────────────────────────────────
// Header copy for the Filters drawer (Figma node 853:3751). The drawer body reuses campaign
// creation's BrandSafetySeverityPanel, so the flag taxonomy / severity model / state shape all come
// from Persona/BrandSafety/brandSafetyConsts.js — the single source of truth — not from here.
export const BRAND_SAFETY_PARAMETERS_COPY = {
  title: 'Brand Safety Parameters',
  description:
    "Applies to this influencer's whole Brand Safety page - the report and the review queue update instantly.",
};

/** Tooltip on a toggle the campaign has turned off — campaign preferences outrank influencer ones. */
export const BRAND_SAFETY_CAMPAIGN_LOCK_REASON =
  "This flag is turned off for the campaign. Enable it in the campaign's Brand Safety settings first.";

// ─── Report tabs ──────────────────────────────────────────────────────────────
export const REPORT_TAB = {
  REPORT: 'report',
  REVIEW: 'review',
};

export const REPORT_TABS = [
  { key: REPORT_TAB.REPORT, label: 'Report' },
  { key: REPORT_TAB.REVIEW, label: 'Review' },
];

// ─── Export ───────────────────────────────────────────────────────────────────
// PDF is the only supported format, so the header renders a single button rather than a menu.
export const EXPORT_LABEL = 'Export';
export const EXPORT_BUSY_LABEL = 'Preparing…';

/** Placeholder shown wherever a metric is absent, matching the Audiences influencer table. */
export const DASH = '–';
